import { Container, Graphics, Text } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette, PROC_COLOR } from '../render/Palette';
import { ARTIFACTS_BY_ID } from '../sim/artifacts/ArtifactDefs';
import { CURSES_BY_ID } from '../sim/artifacts/CurseDefs';
import { ABILITY_DEFS, skillParams } from '../sim/abilities/AbilityDefs';
import type { StatId } from '../sim/stats/StatSheet';
import type { RunState } from '../sim/run/RunState';
import type { Player } from '../sim/entities/Player';

const PANEL_W = 680;
const COL_GAP = 24;
const PAD = 26;
// Asymmetric columns: artifact rows (name + Q% + status) run long, stat rows
// (label + number) don't.
const ART_COL_W = 350;
const STAT_COL_W = PANEL_W - PAD * 2 - COL_GAP - ART_COL_W;

/** Display order + labels; pct stats render as signed % vs the 1.0 base. */
const STAT_ROWS: { stat: StatId; label: string; kind: 'pct' | 'flat'; unit?: string }[] = [
  { stat: 'damageMult', label: 'Damage', kind: 'pct' },
  { stat: 'cooldownMult', label: 'Cooldowns', kind: 'pct' },
  { stat: 'areaMult', label: 'Area', kind: 'pct' },
  { stat: 'projSpeedMult', label: 'Proj. speed', kind: 'pct' },
  { stat: 'gemGainMult', label: 'Gem gain', kind: 'pct' },
  { stat: 'moveSpeed', label: 'Move speed', kind: 'flat', unit: ' px/s' },
  { stat: 'magnetRange', label: 'Magnet', kind: 'flat', unit: ' px' },
];

function fmtPct(v: number): string {
  const pct = Math.round((v - 1) * 100);
  return pct === 0 ? '±0%' : `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%`;
}

/**
 * View-only mid-run summary: collected artifacts (with banked/at-risk status),
 * resolved stats, and ability levels. Sim frozen beneath (blocksUpdateBelow).
 */
export class RunOverviewOverlay implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private acceptDelay = 8;

  constructor(private readonly ctx: GameContext, runState: RunState, player: Player) {
    const w = ctx.app.screen.width;
    const h = ctx.app.screen.height;

    const left = this.buildArtifacts(runState);
    const right = this.buildStatsAndAbilities(runState, player);

    const panelH = Math.min(
      h - 40,
      Math.max(left.height, right.height) + 110,
    );
    const px = (w - PANEL_W) / 2;
    const py = (h - panelH) / 2;

    this.root.addChild(new Graphics().rect(0, 0, w, h).fill({ color: 0x05060c, alpha: 0.78 }));
    this.root.addChild(new Graphics()
      .roundRect(px, py, PANEL_W, panelH, 10)
      .fill(Palette.hudPanel)
      .stroke({ width: 2, color: Palette.gold, alpha: 0.6 }));

    const title = new Text({
      text: `FLOOR ${runState.floor} — THE RUN SO FAR`,
      style: {
        fontFamily: 'monospace', fontSize: 20, fontWeight: 'bold',
        fill: Palette.hudText, letterSpacing: 3,
      },
    });
    title.anchor.set(0.5, 0);
    title.position.set(w / 2, py + 18);
    this.root.addChild(title);

    left.position.set(px + PAD, py + 62);
    right.position.set(px + PAD + ART_COL_W + COL_GAP, py + 62);
    this.root.addChild(left, right);

    const closeKeys = 'close  [Tab / I / Esc]';
    const hint = new Text({
      text: closeKeys,
      style: { fontFamily: 'monospace', fontSize: 12, fill: Palette.hudDim },
    });
    hint.anchor.set(0.5);
    hint.position.set(w / 2, py + panelH - 20);
    this.root.addChild(hint);
    this.hint = hint;
  }

  private hint: Text;

  private buildArtifacts(runState: RunState): Container {
    const c = new Container();
    c.addChild(sectionHeader('ARTIFACTS'));
    let y = 26;
    if (runState.artifacts.length === 0) {
      const none = new Text({
        text: 'nothing found yet',
        style: { fontFamily: 'monospace', fontSize: 13, fill: Palette.hudDim },
      });
      none.position.set(0, y);
      c.addChild(none);
      return c;
    }
    for (const a of runState.artifacts) {
      const def = ARTIFACTS_BY_ID.get(a.artifactId);
      const bankedQ = runState.bankedQuality.get(a.artifactId);
      const status = runState.broughtIds.has(a.artifactId)
        ? { text: 'brought — safe', color: Palette.playerCool }
        : bankedQ !== undefined && a.quality <= bankedQ
          ? { text: 'banked', color: Palette.hudDim }
          : { text: 'at risk', color: Palette.gold };
      const prefix = a.proc === 'none' ? '' : `${a.proc.toUpperCase()} `;
      const name = new Text({
        text: `${prefix}${def?.name ?? a.artifactId}  Q ${Math.round(a.quality * 100)}%`,
        style: { fontFamily: 'monospace', fontSize: 14, fill: PROC_COLOR[a.proc] },
      });
      name.position.set(0, y);
      const state = new Text({
        text: status.text,
        style: { fontFamily: 'monospace', fontSize: 11, fill: status.color },
      });
      state.anchor.set(1, 0);
      state.position.set(ART_COL_W, y + 2);
      const curse = a.curseId !== null ? CURSES_BY_ID.get(a.curseId) : undefined;
      const descText = (def?.desc ?? '') +
        (curse !== undefined ? `  /  ${curse.name}: ${curse.desc}` : '');
      const desc = new Text({
        text: descText,
        style: {
          fontFamily: 'monospace', fontSize: 11,
          fill: curse !== undefined ? Palette.enemyHot : Palette.hudDim,
        },
      });
      desc.position.set(0, y + 17);
      c.addChild(name, state, desc);
      y += 40;
    }
    return c;
  }

  private buildStatsAndAbilities(runState: RunState, player: Player): Container {
    const c = new Container();
    c.addChild(sectionHeader('STATS'));
    let y = 26;
    const rows: [string, string][] = [
      ['HP', `${player.hp} / ${player.maxHp}`],
      ...STAT_ROWS.map(({ stat, label, kind, unit }): [string, string] => {
        const v = runState.stats.resolve(stat);
        return [label, kind === 'pct' ? fmtPct(v) : `${Math.round(v)}${unit ?? ''}`];
      }),
    ];
    for (const [label, value] of rows) {
      const l = new Text({
        text: label,
        style: { fontFamily: 'monospace', fontSize: 13, fill: Palette.hudDim },
      });
      l.position.set(0, y);
      const v = new Text({
        text: value,
        style: { fontFamily: 'monospace', fontSize: 13, fill: Palette.hudText },
      });
      v.anchor.set(1, 0);
      v.position.set(STAT_COL_W, y);
      c.addChild(l, v);
      y += 19;
    }

    y += 14;
    const abilitiesHeader = sectionHeader('SKILLS');
    abilitiesHeader.position.set(0, y);
    c.addChild(abilitiesHeader);
    y += 26;
    for (const inst of runState.abilities) {
      const def = ABILITY_DEFS[inst.defId];
      const sig = def.signature ? ' ◆' : '';
      const up = inst.upgraded ? `  ★ ${def.upgradeName}` : '';
      const name = new Text({
        text: `${def.name}${sig}${up}`,
        style: { fontFamily: 'monospace', fontSize: 13, fill: Palette.hudText },
      });
      name.position.set(0, y);
      const desc = new Text({
        text: def.desc(skillParams(inst), inst.upgraded),
        style: { fontFamily: 'monospace', fontSize: 11, fill: Palette.hudDim },
      });
      desc.position.set(0, y + 16);
      c.addChild(name, desc);
      y += 36;
    }
    return c;
  }

  enter(): void {}
  exit(): void {}

  update(_dt: number, input: InputSnapshot): void {
    this.hint.text = input.device === 'pad' ? 'close  [⧉ / Ⓑ]' : 'close  [Tab / I / Esc]';
    if (this.acceptDelay > 0) {
      this.acceptDelay--;
      return;
    }
    if (input.overviewPressed || input.cancelPressed || input.pausePressed) {
      this.ctx.scenes.pop();
    }
  }

  render(_alpha: number): void {}
}

function sectionHeader(label: string): Text {
  return new Text({
    text: label,
    style: {
      fontFamily: 'monospace', fontSize: 13, fontWeight: 'bold',
      fill: Palette.gold, letterSpacing: 2,
    },
  });
}
